import { useState } from 'react';
import bs58 from 'bs58';
import { providers, Wallet } from 'ethers';
import { Keypair } from '@solana/web3.js';

import { rpcUrls, urlRegex } from '../../utils/rpc-urls';

import '../Styles/Settings.css';
import SolWalletGenerator from '../GenerateSolWallet';

const hidePrivateKey = (value: string, masked: boolean): string => {
    return masked ? '*'.repeat(value.length) : value;
};

export function Settings({
    solInitialBuyArrow,
    onSolInitialBuyArrowChange,
    solInitialBuyCopy,
    onSolInitialBuyCopyChange,

    ethLiquidityArrow,
    onEthLiquidityArrowChange,
    ethLiquidityCopy,
    onEthLiquidityCopyChange,

    solSnipeArrow,
    onSolSnipeArrowChange,
    solSnipeCopy,
    onSolSnipeCopyChange,

    ethSnipeArrow,
    onEthSnipeArrowChange,
    ethSnipeCopy,
    onEthSnipeCopyChange,

    solBuyPrio,
    onSolBuyPrioChange,
    solSellPrio,
    onSolSellPrioChange,

    autoSellDelayArrow,
    onAutoSellDelayArrowChange,
    autoSellProfitArrow,
    onAutoSellProfitArrowChange,
    autoSellDelayCreation,
    onAutoSellDelayCopyChange,
    autoSellProfitCreation,
    onAutoSellProfitCopyChange,
    ethPrivateKey,
    onEthPrivateKeyChange,
    solanaPrivateKey,
    onSolanaPrivateKeyChange,
    solanaSniperPrivateKey,
    onSolanaSniperPrivateKeyChange,
    ethSniperPrivateKey,
    onEthSniperPrivateKeyChange,
    zeroSlotSellTip,
    onZeroSlotSellTipChange,
    zeroSlotBuyTip,
    onZeroSlotBuyTipChange,

    solRpcUrl,
    onSolRpcUrlChange,
}: {
    solInitialBuyArrow: number;
    onSolInitialBuyArrowChange: (value: number) => void;
    solInitialBuyCopy: number;
    onSolInitialBuyCopyChange: (value: number) => void;

    ethLiquidityArrow: number;
    onEthLiquidityArrowChange: (value: number) => void;
    ethLiquidityCopy: number;
    onEthLiquidityCopyChange: (value: number) => void;

    solSnipeArrow: number;
    onSolSnipeArrowChange: (value: number) => void;
    solSnipeCopy: number;
    onSolSnipeCopyChange: (value: number) => void;

    ethSnipeArrow: number;
    onEthSnipeArrowChange: (value: number) => void;
    ethSnipeCopy: number;
    onEthSnipeCopyChange: (value: number) => void;

    solBuyPrio: number;
    onSolBuyPrioChange: (value: number) => void;

    solSellPrio: number;
    onSolSellPrioChange: (value: number) => void;

    autoSellDelayArrow: number;
    onAutoSellDelayArrowChange: (value: number) => void;

    autoSellProfitArrow: number;
    onAutoSellProfitArrowChange: (value: number) => void;

    autoSellDelayCreation: number;
    onAutoSellDelayCopyChange: (value: number) => void;

    autoSellProfitCreation: number;
    onAutoSellProfitCopyChange: (value: number) => void;

    ethPrivateKey: string;
    onEthPrivateKeyChange: (value: string) => void;

    solanaPrivateKey: string;
    onSolanaPrivateKeyChange: (value: string) => void;

    solanaSniperPrivateKey: string;
    onSolanaSniperPrivateKeyChange: (value: string) => void;

    ethSniperPrivateKey: string;
    onEthSniperPrivateKeyChange: (value: string) => void;

    zeroSlotSellTip: number;
    onZeroSlotSellTipChange: (value: number) => void;

    zeroSlotBuyTip: number;
    onZeroSlotBuyTipChange: (value: number) => void;

    solRpcUrl: string;
    onSolRpcUrlChange: (url: string) => void;
}) {
    let isKeyValid = {
        solana: false,
        solanaSniper: false,
        eth: false,
        ethSniper: false,
    };

    let isKeyMasked = {
        solana: false,
        solanaSniper: false,
        eth: false,
        ethSniper: false,
    };

    try {
        new Wallet(ethPrivateKey, new providers.JsonRpcProvider(rpcUrls['mainnet']));
        isKeyValid = { ...isKeyValid, eth: true };
        isKeyMasked = { ...isKeyMasked, eth: true };
    } catch {
        isKeyValid = { ...isKeyValid, eth: false };
        isKeyMasked = { ...isKeyMasked, eth: false };
    }

    try {
        Keypair.fromSecretKey(bs58.decode(solanaPrivateKey));
        isKeyValid = { ...isKeyValid, solana: true };
        isKeyMasked = { ...isKeyMasked, solana: true };
    } catch {
        isKeyValid = { ...isKeyValid, solana: false };
        isKeyMasked = { ...isKeyMasked, solana: false };
    }

    try {
        Keypair.fromSecretKey(bs58.decode(solanaSniperPrivateKey));
        isKeyValid = { ...isKeyValid, solanaSniper: true };
        isKeyMasked = { ...isKeyMasked, solanaSniper: true };
    } catch {
        isKeyValid = { ...isKeyValid, solanaSniper: false };
        isKeyMasked = { ...isKeyMasked, solanaSniper: false };
    }

    try {
        new Wallet(ethSniperPrivateKey, new providers.JsonRpcProvider(rpcUrls['mainnet']));
        isKeyValid = { ...isKeyValid, ethSniper: true };
        isKeyMasked = { ...isKeyMasked, ethSniper: true };
    } catch {
        isKeyValid = { ...isKeyValid, ethSniper: false };
        isKeyMasked = { ...isKeyMasked, ethSniper: false };
    }

    const [solRpcUrlInputValue, setSolRpcUrlInputValue] = useState<string>(solRpcUrl);

    return (
        <section className='settings-container'>
            <section className='settings-key-input-wrap'>
                <div>
                    <label>
                        <b>SOL RPC URL</b>
                        <input
                            value={solRpcUrlInputValue}
                            onChange={(e) => {
                                setSolRpcUrlInputValue(e.target.value);
                            }}
                        ></input>
                    </label>
                    {urlRegex.test(solRpcUrlInputValue) ? <span className='checked-icon'></span> : <span className='unchecked-icon'></span>}
                    <button
                        className='settings-clear-button'
                        disabled={!urlRegex.test(solRpcUrlInputValue)}
                        onClick={() => {
                            onSolRpcUrlChange(solRpcUrlInputValue);
                        }}
                    >
                        APPLY
                    </button>
                </div>
                <div>
                    <label>
                        <b>SOL Private Key</b>
                        <input
                            value={hidePrivateKey(solanaPrivateKey, isKeyMasked.solana)}
                            onChange={(e) => {
                                onSolanaPrivateKeyChange(e.target.value);
                            }}
                        />
                    </label>

                    {isKeyValid.solana ? <span className='checked-icon'></span> : <span className='unchecked-icon'></span>}
                    <button
                        className='settings-clear-button'
                        onClick={() => {
                            onSolanaPrivateKeyChange('');
                        }}
                    >
                        CLEAR
                    </button>

                    {!solanaPrivateKey && <SolWalletGenerator setPrivateKey={onSolanaPrivateKeyChange} />}
                </div>
                <div>
                    <label>
                        <b>SOL Sniper Private Key</b>
                        <input
                            value={hidePrivateKey(solanaSniperPrivateKey, isKeyMasked.solanaSniper)}
                            onChange={(e) => {
                                onSolanaSniperPrivateKeyChange(e.target.value);
                            }}
                        ></input>
                    </label>
                    {isKeyValid.solanaSniper ? <span className='checked-icon'></span> : <span className='unchecked-icon'></span>}
                    <button
                        className='settings-clear-button'
                        onClick={() => {
                            onSolanaSniperPrivateKeyChange('');
                        }}
                    >
                        CLEAR
                    </button>

                    {!solanaSniperPrivateKey && <SolWalletGenerator setPrivateKey={onSolanaSniperPrivateKeyChange} />}
                </div>
                <div>
                    <label>
                        <b>ERC Private Key</b>
                        <input
                            value={hidePrivateKey(ethPrivateKey, isKeyMasked.eth)}
                            onChange={(e) => {
                                onEthPrivateKeyChange(e.target.value);
                            }}
                        ></input>
                    </label>
                    {isKeyValid.eth ? <span className='checked-icon'></span> : <span className='unchecked-icon'></span>}
                    <button
                        className='settings-clear-button'
                        onClick={() => {
                            onEthPrivateKeyChange('');
                        }}
                    >
                        CLEAR
                    </button>
                </div>
                <div>
                    <label>
                        <b>ERC Sniper Private Key</b>
                        <input
                            value={hidePrivateKey(ethSniperPrivateKey, isKeyMasked.ethSniper)}
                            onChange={(e) => {
                                onEthSniperPrivateKeyChange(e.target.value);
                            }}
                        ></input>
                    </label>
                    {isKeyValid.ethSniper ? <span className='checked-icon'></span> : <span className='unchecked-icon'></span>}
                    <button
                        className='settings-clear-button'
                        onClick={() => {
                            onEthSniperPrivateKeyChange('');
                        }}
                    >
                        CLEAR
                    </button>
                </div>
            </section>
            <hr />
            <section className='sol-buy-init'>
                <h3>SOL Buy Initial</h3>
                <div className='arrow-inp'>
                    <div className='arrow-icon'></div>
                    <input
                        className='arrow'
                        type='number'
                        value={solInitialBuyArrow}
                        onChange={(event) => {
                            onSolInitialBuyArrowChange(Number(event.target.value));
                        }}
                    />
                </div>
                <div className='copy-inp'>
                    <div className='copy-icon'></div>
                    <input
                        className='copy'
                        type='number'
                        value={solInitialBuyCopy}
                        onChange={(event) => {
                            onSolInitialBuyCopyChange(Number(event.target.value));
                        }}
                    />
                </div>
            </section>
            <section className='eth-buy-init'>
                <h3>ETH Liquidity</h3>
                <div className='arrow-inp'>
                    <div className='arrow-icon'></div>
                    <input
                        className='arrow'
                        type='number'
                        value={ethLiquidityArrow}
                        onChange={(event) => {
                            onEthLiquidityArrowChange(Number(event.target.value));
                        }}
                    />
                </div>
                <div className='copy-inp'>
                    <div className='copy-icon'></div>
                    <input
                        className='copy'
                        type='number'
                        value={ethLiquidityCopy}
                        onChange={(event) => {
                            onEthLiquidityCopyChange(Number(event.target.value));
                        }}
                    />
                </div>
            </section>
            <section className='sol-snipe'>
                <h3>SOL Snipe</h3>
                <div className='arrow-inp'>
                    <div className='arrow-icon'></div>
                    <input
                        className='arrow'
                        type='number'
                        value={solSnipeArrow}
                        onChange={(event) => {
                            onSolSnipeArrowChange(Number(event.target.value));
                        }}
                    />
                </div>
                <div className='copy-inp'>
                    <div className='copy-icon'></div>
                    <input
                        className='copy'
                        type='number'
                        value={solSnipeCopy}
                        onChange={(event) => {
                            onSolSnipeCopyChange(Number(event.target.value));
                        }}
                    />
                </div>
            </section>
            <section className='eth-snipe'>
                <h3>ETH Snipe %</h3>
                <div className='arrow-inp'>
                    <div className='arrow-icon'></div>
                    <input
                        className='arrow'
                        type='number'
                        value={ethSnipeArrow}
                        max={100}
                        min={0}
                        onChange={(event) => {
                            onEthSnipeArrowChange(Math.max(0, Math.min(100, Number(event.target.value))));
                        }}
                    />
                </div>
                <div className='copy-inp'>
                    <div className='copy-icon'></div>
                    <input
                        className='copy'
                        type='number'
                        value={ethSnipeCopy}
                        max={100}
                        min={0}
                        onChange={(event) => {
                            onEthSnipeCopyChange(Math.max(0, Math.min(100, Number(event.target.value))));
                        }}
                    />
                </div>
            </section>
            <section className='buy-sell-prio'>
                <label>
                    <b>Buy Prio Fee</b>
                    <input
                        className='buy-sell'
                        type='number'
                        value={solBuyPrio}
                        onChange={(event) => {
                            onSolBuyPrioChange(Number(event.target.value));
                        }}
                    />
                </label>
                <label>
                    <b>Sell Prio Fee</b>
                    <input
                        className='buy-sell'
                        type='number'
                        value={solSellPrio}
                        onChange={(event) => {
                            onSolSellPrioChange(Number(event.target.value));
                        }}
                    />
                </label>
            </section>
            <section className='buy-sell-prio'>
                <label>
                    <b>0Slot buy tip</b>
                    <input
                        className='buy-sell'
                        type='number'
                        value={zeroSlotBuyTip}
                        onChange={(event) => {
                            let value = Number(event.target.value);
                            if (value < 0.0001) value = 0.0001;
                            onZeroSlotBuyTipChange(value);
                        }}
                    />
                </label>
                <label>
                    <b>0Slot sell tip</b>
                    <input
                        className='buy-sell'
                        type='number'
                        value={zeroSlotSellTip}
                        onChange={(event) => {
                            let value = Number(event.target.value);
                            if (value < 0.0001) value = 0.0001;
                            onZeroSlotSellTipChange(value);
                        }}
                    />
                </label>
            </section>
            <h3 className='h3-defaults'>Auto Sell Defaults</h3>
            <section className='defaults'>
                <div className='arrow-defaults'>
                    <div className='icon-inp'>
                        <div className='arrow-icon'></div>
                        <input
                            type='number'
                            className='arrow'
                            value={autoSellDelayArrow}
                            onChange={(event) => {
                                onAutoSellDelayArrowChange(Number(event.target.value));
                            }}
                        />
                        <h3>sec</h3>
                    </div>
                    <div className='inp-text'>
                        <input
                            type='number'
                            className='arrow'
                            value={autoSellProfitArrow}
                            onChange={(event) => {
                                onAutoSellProfitArrowChange(Number(event.target.value));
                            }}
                        />
                        <h3>% profit</h3>
                    </div>
                </div>
                <div className='copy-defaults'>
                    <div className='icon-inp'>
                        <div className='copy-icon'></div>
                        <input
                            type='number'
                            className='copy'
                            value={autoSellDelayCreation}
                            onChange={(event) => {
                                onAutoSellDelayCopyChange(Number(event.target.value));
                            }}
                        />
                        <h3>sec</h3>
                    </div>
                    <div className='inp-text'>
                        <input
                            type='number'
                            className='copy'
                            value={autoSellProfitCreation}
                            onChange={(event) => {
                                onAutoSellProfitCopyChange(Number(event.target.value));
                            }}
                        />
                        <h3>% profit</h3>
                    </div>
                </div>
            </section>
        </section>
    );
}
